import { FieldInfo } from "@/features/data/field-info";
import { useIngestionSchedule, useLastReceived, useMonitorVerdict } from "@/hooks/use-pipeline";
import type { Env, EnvKpis, LastReceivedValue, PipelineVerdictStatus } from "@/lib/types";

const ENV_STYLE = {
  dev: {
    accent: "var(--iris-neon-cyan)",
    bg: "rgba(var(--iris-env-dev-rgb),0.06)",
    border: "rgba(var(--iris-env-dev-rgb),0.12)",
  },
  prod: {
    accent: "var(--iris-neon-purple)",
    bg: "rgba(var(--iris-env-prod-rgb),0.06)",
    border: "rgba(var(--iris-env-prod-rgb),0.12)",
  },
} as const;

// IR-1034 C: full per-system indicator set (Rotem — "the indicators row should be
// shown per system"). NOTE: `groups` is currently a TOTAL count (BE
// compute_kpis counts all group_application_data rows) — Rotem wants MONITORED
// groups; that needs a BE change + her definition (tracked in the plan doc).
// IR-1584 r3 (Rotem, comment 64669): "System Coverage" -> "Connected /
// Running Avatars" and "Event Backlog" -> "Folder Backlog", each with an
// info tooltip. `tooltip` is undefined for tiles Rotem didn't ask about.
const KPI_CONFIG: { key: keyof EnvKpis; label: string; tooltip?: string }[] = [
  { key: "groups", label: "Groups" },
  { key: "entities", label: "Entities" },
  { key: "ingestion_rate", label: "Ingestion Rate" },
  { key: "active_alerts", label: "Active Alerts" },
  {
    key: "scraper_coverage",
    label: "Connected / Running Avatars",
    tooltip:
      "How many of our collection avatars (Telegram, WhatsApp, Signal) are connected and reporting as running on the collection server right now, out of all configured avatars. The percentage is the same figure as a share.",
  },
  {
    key: "bucket_backlog",
    label: "Folder Backlog",
    tooltip:
      "How many scrape-result folders are waiting right now to be picked up by ingestion. A live snapshot, not a count over time. A low number is healthy; a number that keeps growing means ingestion is falling behind.",
  },
];

function formatKpi(kpis: EnvKpis | null, key: keyof EnvKpis): string {
  const kpi = kpis?.[key];
  if (!kpi || kpi.value == null) return "—";
  return `${kpi.value.toLocaleString()}${kpi.unit ? ` ${kpi.unit}` : ""}`;
}

// IR-1584 r3: the "Connected / Running Avatars" tile's main value is the
// count (active/total), percentage secondary — Rotem: "I need the number,
// percentage should be smaller". Three cases, orchestrator ruling
// 2026-10-16:
//   1. active/total present, total > 0  -> count main, "NN%" secondary.
//   2. total === 0, or either is null     -> Could Not Check (Ingestion
//      Matrix law: Unknown/Could Not Check is never zero).
//   3. fields absent entirely (older backend predating this change)
//      -> fall back to the old percentage-only rendering, so an ops-before-
//      backend deploy order stays safe.
function CoverageTileValue({ kpi }: { kpi: EnvKpis["scraper_coverage"] | undefined }) {
  // No EnvKpis payload at all (e.g. still loading, fetch failed) — same "—"
  // every other tile in this grid shows via formatKpi for the same reason.
  // Could Not Check is reserved for an actual coverage snapshot that says
  // "I have no active/total to report", not for "I have no payload yet".
  if (!kpi) return <>—</>;
  const fieldsPresent = "active" in kpi && "total" in kpi;
  if (!fieldsPresent) {
    // Case 3: older backend — no active/total keys at all.
    return <>{formatKpi({ scraper_coverage: kpi } as EnvKpis, "scraper_coverage")}</>;
  }
  const { active, total } = kpi;
  if (active == null || total == null || total === 0) {
    // Case 2: no snapshot yet, or a genuinely empty fleet.
    return <>Could Not Check</>;
  }
  // Case 1.
  const pct = Math.round((active / total) * 100);
  return (
    <>
      <span>
        {active} / {total}
      </span>
      <span className="ml-1.5 text-[11px] font-normal text-[var(--iris-fg-3)]">{pct}%</span>
    </>
  );
}

// IR-1034 QA (Rotem — "I don't see the ingestion times … place the ingestion
// status up, next to each environment"): surface the last + next ingestion
// cycle inside each env frame.
//
// IR-1584 (a) (Rotem, 2026-09-15, comment 62422: "Can you display IL time
// and not UTC?"): Israel time, not a fixed offset — Asia/Jerusalem is
// UTC+3 (IDT) roughly Mar-Oct and UTC+2 (IST) the rest of the year, so this
// goes through Intl's IANA timezone database rather than a hardcoded "+3"
// that would be wrong half the year.
const IL_TIME_ZONE = "Asia/Jerusalem";

const ilClockFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: IL_TIME_ZONE,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

export function fmtClock(iso: string): string {
  const d = new Date(iso);
  return `${ilClockFormatter.format(d)} IL`;
}

function relFuture(iso: string): string {
  const mins = Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
  if (mins <= 0) return "due now";
  if (mins < 60) return `in ${mins}m`;
  return `in ${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function relPast(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return hrs < 24 ? `${hrs}h ago` : `${Math.floor(hrs / 24)}d ago`;
}

// IR-1034 QA (Rotem — "what are the markings 0 / 1"): the schedule field is a raw
// cron expression ("0 */1 * * *"). Show a plain-English cadence instead; if it's
// an unrecognized cron, omit it rather than surface the raw string.
export function humanizeSchedule(cron: string | null): string {
  if (!cron) return "";
  const c = cron.trim();
  const everyNHours = c.match(/^0 \*\/(\d+) \* \* \*$/);
  if (everyNHours) {
    const n = Number(everyNHours[1]);
    return n === 1 ? "hourly" : `every ${n}h`;
  }
  if (c === "0 * * * *") return "hourly";
  return "";
}

function EnvIngestion({ env, style }: { env: Env; style: (typeof ENV_STYLE)[Env] }) {
  const { data, isLoading } = useIngestionSchedule(env);
  return (
    <div
      className="rounded-[10px] p-3"
      style={{ background: style.bg, border: `1px solid ${style.border}` }}
    >
      <div className="text-[10px] text-[var(--iris-fg-3)]">Ingestion</div>
      {isLoading ? (
        <div className="iris-meta mt-1">Loading…</div>
      ) : !data || data.degraded ? (
        <div className="iris-meta mt-1">Schedule unavailable</div>
      ) : (
        <>
          <div className="text-[15px] font-bold tabular-nums text-[var(--iris-fg-1)]">
            Next {data.next_run_at ? relFuture(data.next_run_at) : "—"}
            {data.next_run_at && (
              <span className="ml-2 text-[11px] font-normal text-[var(--iris-fg-3)]">
                {fmtClock(data.next_run_at)}
              </span>
            )}
          </div>
          <div className="iris-meta mt-0.5">
            Last ran {data.last_run_at ? relPast(data.last_run_at) : "—"}
            {humanizeSchedule(data.schedule) ? ` · ${humanizeSchedule(data.schedule)}` : ""}
          </div>
        </>
      )}
    </div>
  );
}

// IR-1584 (c): the pipeline monitor's own verdict (Rotem 2026-09-13/14 —
// "the Pipeline tab there will show the monitor's own verdict for each
// environment ... Dev and prod are each read from their own data"). This is
// a SEPARATE fact from HealthBanner's shared SCRAPE→INDEX pipeline health
// (Yaniv, 2026-09-15: "the green HEALTHY ... is not the pipeline monitor's
// verdict. It is a separate calculation").
//
// A report older than STALE_MINUTES renders as stale rather than trusting a
// frozen "healthy" (Yaniv's plan comment, 2026-09-14: "A stale report is
// shown as stale, not as healthy").
const STALE_MINUTES = 15;

function isStaleReport(reportAt: string | null): boolean {
  if (!reportAt) return false;
  const ageMinutes = (Date.now() - new Date(reportAt).getTime()) / 60_000;
  return ageMinutes > STALE_MINUTES;
}

const VERDICT_LABEL: Record<PipelineVerdictStatus, string> = {
  healthy: "Healthy",
  degraded: "Degraded",
  critical: "Critical",
  could_not_check: "Could Not Check",
};

const VERDICT_COLOR: Record<PipelineVerdictStatus, string> = {
  healthy: "var(--iris-status-ok, #3ddc97)",
  degraded: "var(--iris-status-warn, #ff9933)",
  critical: "var(--iris-status-err, #ff4dd2)",
  could_not_check: "var(--iris-fg-4)",
};

function MonitorVerdict({ env, style }: { env: Env; style: (typeof ENV_STYLE)[Env] }) {
  const { data, isLoading } = useMonitorVerdict();
  const verdict = data?.[env];
  const stale = verdict ? isStaleReport(verdict.report_at) : false;
  // A stale report is never shown as its last-known "healthy" — the
  // Ingestion Matrix's Could Not Check reads the same way an unreadable
  // report does (Iron Law 11 rule 4: Could Not Check is never 0 / healthy).
  const effectiveStatus: PipelineVerdictStatus | null = !verdict
    ? null
    : stale
      ? "could_not_check"
      : verdict.overall_status;

  return (
    <div
      className="rounded-[10px] p-3"
      style={{ background: style.bg, border: `1px solid ${style.border}` }}
    >
      <div className="text-[10px] text-[var(--iris-fg-3)]">Pipeline Monitor Verdict</div>
      {isLoading || !verdict ? (
        <div className="iris-meta mt-1">Loading…</div>
      ) : (
        <>
          <div
            className="text-[15px] font-bold tabular-nums mt-1"
            style={{ color: VERDICT_COLOR[effectiveStatus ?? "could_not_check"] }}
          >
            {VERDICT_LABEL[effectiveStatus ?? "could_not_check"]}
            {stale && (
              <span className="ml-2 text-[11px] font-normal text-[var(--iris-fg-3)]">
                stale report
              </span>
            )}
          </div>
          {verdict.error && <div className="iris-meta mt-0.5">{verdict.error}</div>}
          {verdict.issues.length > 0 && (
            <ul className="mt-0.5 list-disc pl-4">
              {verdict.issues.map((issue) => (
                <li key={issue} className="iris-meta">
                  {issue}
                </li>
              ))}
            </ul>
          )}
          {verdict.report_at && (
            <div className="iris-meta mt-0.5">
              Report {relPast(verdict.report_at)} ({fmtClock(verdict.report_at)})
            </div>
          )}
        </>
      )}
    </div>
  );
}

// IR-1584 (b): last data received, per platform type (telegram/whatsapp/
// signal — the Ingestion Matrix platform vocabulary), per environment
// (Rotem, comment 62422, option B).
const PLATFORM_LABEL: Record<"telegram" | "whatsapp" | "signal", string> = {
  telegram: "Telegram",
  whatsapp: "WhatsApp",
  signal: "Signal",
};

function formatLastReceived(value: LastReceivedValue): string {
  if (value === "could_not_check") return "Could Not Check";
  if (value === null) return "—";
  return relPast(value);
}

function LastReceivedPanel({ env, style }: { env: Env; style: (typeof ENV_STYLE)[Env] }) {
  const { data, isLoading } = useLastReceived();
  const byPlatform = data?.[env];
  return (
    <div
      className="mt-2 rounded-[10px] p-3"
      style={{ background: style.bg, border: `1px solid ${style.border}` }}
    >
      <div className="text-[10px] text-[var(--iris-fg-3)]">Last Data Received</div>
      {isLoading || !byPlatform ? (
        <div className="iris-meta mt-1">Loading…</div>
      ) : (
        <div className="mt-1 grid grid-cols-3 gap-1" data-testid="last-received-row">
          {(["telegram", "whatsapp", "signal"] as const).map((platform) => (
            <div key={platform} className="flex flex-col items-center gap-0.5">
              <span className="iris-meta">{PLATFORM_LABEL[platform]}</span>
              <span
                className="text-[12px] font-semibold tabular-nums"
                style={{
                  color:
                    byPlatform[platform] === "could_not_check"
                      ? "var(--iris-fg-4)"
                      : "var(--iris-fg-1)",
                }}
              >
                {formatLastReceived(byPlatform[platform])}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// IR-1771 FE: the Active Alerts tile's Could Not Check count, its own labelled
// value — not in the headline total. Three cases (Ingestion Matrix law):
//   number -> "Could Not Check N" (a measured 0 is a real 0);
//   null   -> "Could Not Check Unknown" (unmeasured, never 0);
//   absent -> nothing (older backend; the tile reads as before).
function CouldNotCheckMeta({ kpi }: { kpi: EnvKpis["active_alerts"] | undefined }) {
  const n = kpi?.could_not_check;
  if (n === undefined) return null;
  return (
    <div className="iris-meta mt-0.5 text-[9px]" data-testid="active-alerts-cnc">
      Could Not Check {n === null ? "Unknown" : n.toLocaleString()}
    </div>
  );
}

export function EnvKpiGrid({ env, kpis }: { env: Env; kpis: EnvKpis | null }) {
  const style = ENV_STYLE[env];
  return (
    <div className="flex-1">
      <div
        className="mb-2 pl-1 text-[10px] uppercase tracking-widest"
        style={{ color: style.accent }}
      >
        {env === "dev" ? "Development" : "Production"}
      </div>
      <div className="grid grid-cols-3 gap-1.5" data-testid="env-kpi-tiles">
        {KPI_CONFIG.map(({ key, label, tooltip }) => (
          <div
            key={key}
            data-kpi-tile={key}
            className="rounded-[10px] p-2"
            style={{ background: style.bg, border: `1px solid ${style.border}` }}
          >
            <div className="flex items-center text-[9px] text-[var(--iris-fg-3)]">
              {label}
              {tooltip && <FieldInfo text={tooltip} />}
            </div>
            <div
              data-kpi-value
              className="text-[16px] font-bold tabular-nums text-[var(--iris-fg-1)]"
            >
              {key === "scraper_coverage" ? (
                <CoverageTileValue kpi={kpis?.scraper_coverage} />
              ) : (
                formatKpi(kpis, key)
              )}
            </div>
            {key === "active_alerts" && <CouldNotCheckMeta kpi={kpis?.active_alerts} />}
          </div>
        ))}
      </div>
      {/* IR-1584 r2 (Rotem, comment 64255): Ingestion and Pipeline Monitor
          Verdict on the same row. */}
      <div className="mt-2 grid grid-cols-2 gap-2" data-testid="env-ingestion-verdict-row">
        <EnvIngestion env={env} style={style} />
        <MonitorVerdict env={env} style={style} />
      </div>
      <LastReceivedPanel env={env} style={style} />
    </div>
  );
}
